import * as PDFJS from 'pdfjs-dist'
import PDFWorker from 'pdfjs-dist/build/pdf.worker.min?url'
import { shallowRef, isRef, watch } from 'vue'
import { i18n } from '@/i18n'

function configWorker(wokerSrc) {
  PDFJS.GlobalWorkerOptions.workerSrc = wokerSrc
}

export function usePDF(
  src,
  options = {
    onProgress: undefined,
    onPassword: undefined,
    onError: undefined,
    password: '',
  }
) {
  if (!PDFJS.GlobalWorkerOptions?.workerSrc) configWorker(PDFWorker)

  const pdf = shallowRef()
  const pdfDoc = shallowRef()
  const pages = shallowRef(0)
  const info = shallowRef({})
  let loadingTaskRef = null

  function processLoadingTask(source) {
    if (pdfDoc.value) {
      pdfDoc.value.destroy()
      pdfDoc.value = null
    }

    const headers = options.getHttpHeaders?.() ?? options.httpHeaders
    let docSource = source
    if (typeof source === 'string') {
      if (source.startsWith('blob:')) {
        docSource = { url: source }
      } else {
        docSource = { url: source, withCredentials: true }
        if (headers && Object.keys(headers).length) {
          docSource.httpHeaders = headers
        }
      }
    }

    const loadingTask = PDFJS.getDocument(docSource)
    loadingTaskRef = loadingTask
    if (options.onProgress) {
      loadingTask.onProgress = options.onProgress
    }

    if (options.onPassword) {
      loadingTask.onPassword = options.onPassword
    } else if (options.password) {
      const onPassword = updatePassword => {
        updatePassword(options.password ?? '')
      }
      loadingTask.onPassword = onPassword
    }

    loadingTask.promise.then(
      async doc => {
        pdfDoc.value = doc

        pdf.value = doc.loadingTask
        pages.value = doc.numPages

        const metadata = await doc.getMetadata()
        const attachments = await doc.getAttachments()
        const javascript = await doc.getJSActions()
        const outline = await doc.getOutline()

        info.value = {
          metadata,
          attachments,
          javascript,
          outline,
        }
      },
      error => {
        // PDF loading error
        if (typeof options.onError === 'function') options.onError(error)
      }
    )
  }

  async function getPDFDestination(destination) {
    const document = await pdf.value?.promise
    if (!document) return null

    const destArray = await getDestinationArray(document, destination)
    const destRef = await getDestinationRef(document, destArray)
    if (!destRef || !destArray) return null

    const pageIndex = await document.getPageIndex(destRef)

    const name = destArray[1].name
    const rest = destArray.slice(2)

    const location = isSpecLike(rest) ? getLocation(name, rest) : null

    return { pageIndex, location: location ?? { type: 'Fit', spec: [] } }
  }

  async function getLoadedDocument() {
    if (pdfDoc.value) return pdfDoc.value
    if (loadingTaskRef) return await loadingTaskRef.promise
    throw new Error(i18n.global.t('docviewer.notLoaded'))
  }

  async function getLoadedPdfBytes() {
    const doc = await getLoadedDocument()
    if (typeof doc.getData === 'function') {
      return await doc.getData()
    }
    if (typeof doc.saveDocument === 'function') {
      return await doc.saveDocument()
    }
    throw new Error(i18n.global.t('docviewer.notLoaded'))
  }

  async function fetchPdfBytes(url) {
    const headers = options.getHttpHeaders?.() ?? options.httpHeaders ?? {}
    const res = await fetch(url, {
      credentials: 'include',
      headers,
    })
    if (!res.ok) {
      throw new Error(i18n.global.t('docviewer.printFetchFailed', { status: res.status }))
    }
    return new Uint8Array(await res.arrayBuffer())
  }

  async function download(filename = i18n.global.t('docviewer.defaultFilename')) {
    const bytes = await getLoadedPdfBytes()
    const blobBytes = new Blob([bytes], { type: 'application/pdf' })
    const blobUrl = URL.createObjectURL(blobBytes)

    const anchorDownload = document.createElement('a')
    document.body.appendChild(anchorDownload)
    anchorDownload.href = blobUrl
    anchorDownload.download = filename
    anchorDownload.style.display = 'none'
    anchorDownload.click()

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl)
      document.body.removeChild(anchorDownload)
    }, 10)
  }

  /**
   * @param {number} dpi
   * @param {string} filename
   * @param {(canvas: HTMLCanvasElement) => void} [prePrintCb]
   * @param {string|null} [sourceOverride] URL выгрузки со штампом (без ?inline=1)
   */
  async function print(
    dpi = 150,
    filename = i18n.global.t('docviewer.defaultFilename'),
    prePrintCb = () => {},
    sourceOverride = null
  ) {
    const bytes = sourceOverride
      ? await fetchPdfBytes(sourceOverride)
      : await getLoadedPdfBytes()
    const savedLoadingTask = PDFJS.getDocument({ data: bytes })
    savedLoadingTask.onPassword = updatePassword => updatePassword('')
    const savedDocument = await savedLoadingTask.promise

    const PRINT_UNITS = dpi / 72
    const CSS_UNITS = 96 / 72

    const iframe = await createIframe()
    const contentWindow = iframe.contentWindow
    contentWindow.document.title = filename

    const pagesNumbers = [...Array(savedDocument.numPages).keys()].map(val => val + 1)
    const onPage = typeof prePrintCb === 'function' ? prePrintCb : () => {}

    try {
      for (const pageNumber of pagesNumbers) {
        const pageToPrint = await savedDocument.getPage(pageNumber)
        const viewport = pageToPrint.getViewport({ scale: 1 })

        if (pageNumber === 1) {
          addStylesToIframe(
            contentWindow,
            (viewport.width * PRINT_UNITS) / CSS_UNITS,
            (viewport.height * PRINT_UNITS) / CSS_UNITS
          )
        }

        const canvas = document.createElement('canvas')
        canvas.width = viewport.width * PRINT_UNITS
        canvas.height = viewport.height * PRINT_UNITS

        const canvasCloned = canvas.cloneNode()
        contentWindow?.document.body.appendChild(canvasCloned)

        await pageToPrint?.render({
          canvasContext: canvas.getContext('2d'),
          intent: 'print',
          transform: [PRINT_UNITS, 0, 0, PRINT_UNITS, 0, 0],
          viewport,
        }).promise

        onPage(canvas)
        canvasCloned.getContext('2d')?.drawImage(canvas, 0, 0)
      }

      contentWindow?.focus()
      contentWindow?.print()
    } finally {
      document.body.removeChild(iframe)
      savedDocument.destroy?.()
    }
  }

  if (isRef(src)) {
    if (src.value) processLoadingTask(src.value)
    watch(src, () => {
      if (src.value) processLoadingTask(src.value)
    })
  } else {
    if (src) processLoadingTask(src)
  }

  return {
    pdf,
    pages,
    info,
    print,
    download,
    getPDFDestination,
  }
}

async function createIframe() {
  return new Promise(resolve => {
    const iframe = document.createElement('iframe')

    iframe.width = '0px'
    iframe.height = '0px'
    iframe.style.cssText = 'position: absolute; top:0; left:0'
    iframe.style.display = 'none'

    iframe.onload = function () {
      resolve(iframe)
    }
    document.body.appendChild(iframe)
  })
}

/**
 * Стили для печати. Колонтитулы (URL, дата, номер страницы) добавляет браузер
 * в диалоге печати — отключить их кодом нельзя, только в настройках печати.
 *
 * Как отключить колонтитулы в браузере:
 * - Chrome / Edge: Ctrl+P → «Ещё настройки» / «Дополнительные параметры» →
 *   снять галочку «Колонтитулы» (Headers and footers).
 * - Firefox: Ctrl+P → «Параметры страницы…» → снять галочки с верхнего и нижнего колонтитула.
 */
function addStylesToIframe(content, sizeX, sizeY) {
  const style = content.document.createElement('style')
  style.textContent = `
    @page {
      margin: 0;
      size: ${sizeX}pt ${sizeY}pt;
    }
    body {
      margin: 0;
      width: 100%;
    }
    canvas {
      width: ${sizeX}pt;
      height: ${sizeY}pt;
      display: block;
      page-break-after: always;
      page-break-before: avoid;
      page-break-inside: avoid;
    }
    canvas:last-child {
      page-break-after: auto;
    }
  `
  content.document.head.appendChild(style)
}
